import { useProgrammaticFocus } from "@imi/ui/hooks/useProgrammaticFocus.js";
import { safeParseJSON } from "@src/shared/helpers/object";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { use, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { z, ZodRawShape } from "zod/v4";
import { RouteDialogsContext } from "../contexts/RouteDialogsContext";
import { RouteDialogSearchParams } from "./useRouteDialogPath";

type UseRouteDialogPropsWithArgs<T extends ZodRawShape | undefined> = {
  argsSchema?: T extends ZodRawShape ? z.ZodObject<T> : undefined;
  dialogKey?: string;
};

export function useRouteDialogProps<T extends ZodRawShape>(
  props: UseRouteDialogPropsWithArgs<T>,
): {
  args: z.infer<z.ZodObject<T>>;
  dismissible: true;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};
export function useRouteDialogProps(
  props?: UseRouteDialogPropsWithArgs<undefined>,
): {
  args: undefined;
  dismissible: true;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};
export function useRouteDialogProps<T extends ZodRawShape>({
  argsSchema,
}: UseRouteDialogPropsWithArgs<T> = {}) {
  const { setTrigger, trigger } = use(RouteDialogsContext);
  const focus = useProgrammaticFocus();
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const { reset: resetQueries } = useQueryErrorResetBoundary();
  const [searchParams, setSearchParams] = useSearchParams();

  function onOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
      requestAnimationFrame(() => {
        focus(trigger?.current);
      });
      setTrigger(null);
      closeTimeout.current = setTimeout(() => {
        searchParams.delete(RouteDialogSearchParams.PATH);
        searchParams.delete(RouteDialogSearchParams.ARGS);
        setSearchParams(searchParams);
      }, 150);
    }
  }

  useEffect(
    () => () => {
      resetQueries();
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    },
    [resetQueries],
  );

  const args = argsSchema
    ? argsSchema.parse(
        safeParseJSON(searchParams.get(RouteDialogSearchParams.ARGS) || "{}") ||
          {},
      )
    : undefined;

  return {
    args,
    dismissible: true,
    onOpenChange,
    open: isOpen,
  };
}
